import { EventCard, EventCardDensity, StatusTag } from '@cogneed/design-system';
import { useTranslation } from 'react-i18next';
import { LiveSessionDecisionTreeRunFieldsFragment } from '../../../../../../../../../generated/core-api/graphql-react-query';
import useClockTime from '../../../../../../../../../Hooks/useClockTime';
import useDecisionTreeRunContext from '../../../../../../../../Contexts/DecisionTree/DecisionTreeRunContext/useDecisionTreeRunContext';
import { TitleText, MetaText } from '../DetectionGroupListItem/styles';
import ResumeText, { RunBodyText, RunTitleRow } from './styles';

interface DecisionTreeRunListItemProps {
  run: LiveSessionDecisionTreeRunFieldsFragment;
  density?: EventCardDensity;
}

// Decision-tree run variant of the shared EventCard shell (see
// DetectionGroupListItem, whose title/meta text styles this reuses directly
// so the two card types never drift) -- one card per run, badge state
// derived purely from `finishedAt` (there's no separate "paused" state
// server-side: pausing is just navigating away, resuming is navigating back
// to the same run).
export default function DecisionTreeRunListItem(props: DecisionTreeRunListItemProps) {
  const { run, density = 'panel' } = props;
  const { t } = useTranslation();
  const { resume } = useDecisionTreeRunContext();
  const isFinished = !!run.finishedAt;
  const clockTime = useClockTime(run.finishedAt ?? run.createdAt);
  const startedAt = useClockTime(run.createdAt);
  const finishedAt = useClockTime(run.finishedAt ?? 0);

  const badge = (
    <StatusTag
      color={isFinished ? 'success' : 'default'}
      label={isFinished ? t('detections.run.finished') : t('detections.run.inProgress')}
    />
  );

  return (
    <EventCard
      badge={badge}
      density={density}
      accent={!isFinished}
      meta={<MetaText density={density}>{clockTime}</MetaText>}
      onClick={isFinished ? undefined : () => { resume(run.id); }}
      title={(
        <RunTitleRow>
          <TitleText density={density} dimmed={isFinished}>{run.decisionTree?.name}</TitleText>
          {!isFinished && (
            <ResumeText density={density}>{density === 'page' ? t('detections.run.resumeFull') : t('detections.run.resume')}</ResumeText>
          )}
        </RunTitleRow>
      )}
    >
      {(!isFinished || density === 'page') && (
        <>
          <RunBodyText density={density}>{t('detections.run.startedAt', { time: startedAt })}</RunBodyText>
          {isFinished && <RunBodyText density={density}>{t('detections.run.finishedAt', { time: finishedAt })}</RunBodyText>}
        </>
      )}
    </EventCard>
  );
}
